import { useState, useEffect, useRef } from 'react'

import Rollbar from 'utils/rollbar'
import {
  isRoleAllowed,
  saveCustomerIdFromCustomerAuth,
  authAttempted,
  resetAuthAttempt,
} from 'utils/auth'
import { fetchCurrentUser, saveAuthToken, clearAuthToken, terminateSession } from 'services/auth'
import { getValidOidcUser, getUserManager } from 'auth/factsetOidc'
import { updateUnleashUserId } from 'services/unleashClient'
import { factset as factsetConfig } from 'config'

import { ForbiddenError } from 'constants/errors'
import { AUTH_TOKEN_KEY, CUSTOMER_ID } from 'constants/localStorage'
import {
  JUST_LOGGED_OUT_KEY,
  JUST_SESSION_EXPIRED_KEY,
  OIDC_CALLBACK_ERROR_KEY,
} from 'constants/sessionStorage'

// Reads a sessionStorage flag exactly once, clears it if present.
// Used as a lazy useState initializer so each flag is consumed on mount and frozen
// for the hook's lifetime — re-renders cannot flip it back.
function readAndClearSessionFlag(key) {
  const wasSet = Boolean(sessionStorage.getItem(key))
  if (wasSet) {
    sessionStorage.removeItem(key)
  }
  return wasSet
}

function useAuth() {
  const [currentUser, setCurrentUser] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [authFailed, setAuthFailed] = useState(false)
  const [needsLogin, setNeedsLogin] = useState(false)

  // These must be lazy useState initializers, NOT values recomputed on each render. Each one
  // runs its cleanup exactly once on mount and freezes the flag for the hook's lifetime.
  const [isSignedOut] = useState(() => readAndClearSessionFlag(JUST_LOGGED_OUT_KEY))
  const [isSessionExpired] = useState(() => readAndClearSessionFlag(JUST_SESSION_EXPIRED_KEY))
  const [oidcCallbackFailed] = useState(() => readAndClearSessionFlag(OIDC_CALLBACK_ERROR_KEY))

  // Guards against React 18 StrictMode double-firing the effect in dev.
  const sessionInitializedRef = useRef(false)

  useEffect(() => {
    function handleStorageChange(event) {
      if (
        event.key === AUTH_TOKEN_KEY &&
        event.newValue === null &&
        sessionInitializedRef.current
      ) {
        setNeedsLogin(true)
      }
    }
    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  useEffect(() => {
    if (isSignedOut || isSessionExpired || oidcCallbackFailed) {
      setIsLoading(false)
      return
    }

    if (sessionInitializedRef.current) {
      return
    }

    async function initSession() {
      try {
        if (authAttempted()) {
          setAuthFailed(true)
          return
        }

        const storedUser = await getValidOidcUser()
        if (!storedUser) {
          setNeedsLogin(true)
          return
        }
        saveAuthToken(storedUser.access_token)
        await fetchUser()
        sessionInitializedRef.current = true
      } catch (error) {
        setCurrentUser(() => {
          throw error
        })
      } finally {
        setIsLoading(false)
      }
    }

    async function fetchUser() {
      localStorage.removeItem(CUSTOMER_ID)
      const user = await fetchCurrentUser()

      if (!isRoleAllowed(user)) {
        throw new ForbiddenError({ user })
      }

      saveCustomerIdFromCustomerAuth(user.customerAuthorizations)
      Rollbar.configure({ payload: { person: { id: user.id, email: user.email } } })
      updateUnleashUserId(user.id)
      resetAuthAttempt()
      setCurrentUser(user)
    }

    initSession()
  }, [isSignedOut, isSessionExpired, oidcCallbackFailed])

  async function endSession() {
    try {
      await terminateSession()
    } catch {
      // ignore failures terminating the session
    } finally {
      resetAuthAttempt()
      clearAuthToken()
      sessionStorage.setItem(JUST_LOGGED_OUT_KEY, 'true')

      try {
        await getUserManager().removeUser()
      } catch {
        // ignore — OIDC session may not exist
      }

      // PingFederate's startSLO.ping uses TargetResource to specify where to redirect
      // after clearing the IdP session, then sends the user back to the app origin.
      window.location.href = `${factsetConfig.oidcAuthority}/sp/startSLO.ping?TargetResource=${window.location.origin}&InErrorResource=${window.location.origin}`
    }
  }

  function signIn() {
    getUserManager().signinRedirect({ state: { returnTo: window.location.pathname } })
  }

  async function refetchCurrentUser() {
    const user = await fetchCurrentUser()
    setCurrentUser(user)
  }

  return {
    state: {
      isLoading,
      currentUser,
      isSignedOut,
      isSessionExpired,
      oidcCallbackFailed,
      authFailed,
      needsLogin,
    },
    handlers: { endSession, refetchCurrentUser, signIn },
  }
}

export default useAuth
